import React, { useState, useEffect, useRef } from 'react';

/**
 * Format minutes as hh:mm (e.g. 90 => "1:30")
 */
function formatMinutesAsTime(totalMinutes) {
  if (totalMinutes === 0 || totalMinutes === null || totalMinutes === undefined) {
    return '0:00';
  }
  const absMinutes = Math.abs(Math.round(totalMinutes));
  const hours = Math.floor(absMinutes / 60);
  const mins = absMinutes % 60;
  const sign = totalMinutes < 0 ? '-' : '';
  return `${sign}${hours}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Data Quality Dashboard
 * Admin-only dashboard for validating time tracking data during post-launch stabilization
 */
export default function DataQualityDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Project validation state
  const [quoteNumber, setQuoteNumber] = useState('');
  const [projectValidation, setProjectValidation] = useState(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // Job routing comparison state
  const [routingComparison, setRoutingComparison] = useState(null);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [routingError, setRoutingError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Fuzzy quote search state for routing tab
  const [allQuotes, setAllQuotes] = useState([]);
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [showQuoteDropdown, setShowQuoteDropdown] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const quoteSearchRef = useRef(null);
  const quoteDropdownRef = useRef(null);

  useEffect(() => {
    fetchMetrics();
  }, []);

  // Load quotes when routing tab is active and quotes haven't been loaded
  useEffect(() => {
    if (activeTab === 'routing' && allQuotes.length === 0 && !quotesLoading) {
      loadAllQuotes();
    }
  }, [activeTab]);

  // Fuzzy filter quotes when quoteNumber changes
  useEffect(() => {
    if (!quoteNumber.trim()) {
      setFilteredQuotes(allQuotes.slice(0, 50));
      return;
    }
    const term = quoteNumber.toLowerCase();
    const filtered = allQuotes.filter((q) => {
      return (
        (q.quoteNumber || '').toString().toLowerCase().includes(term) ||
        (q.user_Text4 || '').toLowerCase().includes(term) ||
        (q.customerName || '').toLowerCase().includes(term)
      );
    });
    setFilteredQuotes(filtered.slice(0, 50));
  }, [quoteNumber, allQuotes]);

  // Click outside handler for quote dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        quoteDropdownRef.current &&
        !quoteDropdownRef.current.contains(event.target) &&
        quoteSearchRef.current &&
        !quoteSearchRef.current.contains(event.target)
      ) {
        setShowQuoteDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('msal.backendToken');
    return { Authorization: `Bearer ${token}` };
  };

  // Load all quotes for the fuzzy search dropdown
  const loadAllQuotes = async () => {
    try {
      setQuotesLoading(true);
      const res = await fetch('/api/quotes', {
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to load quotes');
      const data = await res.json();
      const quotes = data.quotes || [];
      setAllQuotes(quotes);
      setFilteredQuotes(quotes.slice(0, 50));
      console.log(`Loaded ${quotes.length} quotes for routing search`);
    } catch (err) {
      console.error('Error loading quotes:', err);
    } finally {
      setQuotesLoading(false);
    }
  };

  // Handle selecting a quote from the dropdown
  const handleQuoteSelect = (quote) => {
    setSelectedQuote(quote);
    setQuoteNumber(quote.quoteNumber.toString());
    setShowQuoteDropdown(false);
    // Auto-load the routing comparison
    fetchRoutingComparisonForQuote(quote.quoteNumber.toString());
  };

  // Clear quote selection
  const clearQuoteSelection = () => {
    setSelectedQuote(null);
    setQuoteNumber('');
    setRoutingComparison(null);
    setShowQuoteDropdown(true);
    quoteSearchRef.current?.focus();
  };

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch('/api/reports/data-quality', {
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setMetrics(data);
    } catch (err) {
      console.error('Error fetching data quality metrics:', err);
      setError(err.message || 'Failed to load data quality metrics');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectValidation = async () => {
    if (!quoteNumber.trim()) return;

    try {
      setValidationLoading(true);
      setValidationError(null);
      const res = await fetch(`/api/reports/project-validation/${encodeURIComponent(quoteNumber.trim())}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setProjectValidation(data);
    } catch (err) {
      console.error('Error fetching project validation:', err);
      setValidationError(err.message || 'Failed to load project validation');
      setProjectValidation(null);
    } finally {
      setValidationLoading(false);
    }
  };

  const fetchRoutingComparison = async () => {
    if (!quoteNumber.trim()) return;

    try {
      setRoutingLoading(true);
      setRoutingError(null);
      setSelectedJob(null);
      const res = await fetch(`/api/reports/job-routing-comparison/${encodeURIComponent(quoteNumber.trim())}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRoutingComparison(data);
    } catch (err) {
      console.error('Error fetching routing comparison:', err);
      setRoutingError(err.message || 'Failed to load routing comparison');
      setRoutingComparison(null);
    } finally {
      setRoutingLoading(false);
    }
  };

  // Fetch routing comparison for a specific quote (used by dropdown select)
  const fetchRoutingComparisonForQuote = async (quoteNum) => {
    if (!quoteNum) return;

    try {
      setRoutingLoading(true);
      setRoutingError(null);
      setSelectedJob(null);
      const res = await fetch(`/api/reports/job-routing-comparison/${encodeURIComponent(quoteNum)}`, {
        headers: getAuthHeaders()
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setRoutingComparison(data);
    } catch (err) {
      console.error('Error fetching routing comparison:', err);
      setRoutingError(err.message || 'Failed to load routing comparison');
      setRoutingComparison(null);
    } finally {
      setRoutingLoading(false);
    }
  };

  const handlePrintJob = (job) => {
    const printWindow = window.open('', '_blank');
    const projectName = routingComparison?.summary?.projectName || '';
    const quoteNum = routingComparison?.summary?.quoteNumber || '';

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Job Report - ${job.jobNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          h2 { font-size: 14px; color: #666; margin-bottom: 20px; }
          .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 20px; }
          .summary-item { text-align: center; padding: 10px; background: #f5f5f5; border-radius: 4px; }
          .summary-item .value { font-size: 20px; font-weight: bold; }
          .summary-item .label { font-size: 11px; color: #666; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
          th { background: #f0f0f0; font-weight: 600; }
          .status-ok { color: #059669; }
          .status-missing { color: #dc2626; }
          .status-in-progress { color: #2563eb; }
          .status-over-time { color: #d97706; }
          .status-unmatched { color: #7c3aed; }
          .variance-positive { color: #dc2626; }
          .variance-negative { color: #059669; }
          .meta { font-size: 11px; color: #666; margin-bottom: 10px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Job: ${job.jobNumber}</h1>
        <h2>${projectName} | Quote: ${quoteNum}</h2>
        <div class="meta">
          <strong>Part:</strong> ${job.partNumber} | <strong>Qty:</strong> ${job.qty} | <strong>Order:</strong> ${job.orderNumber}
        </div>

        <div class="summary">
          <div class="summary-item">
            <div class="value">${job.summary.expectedHours}h</div>
            <div class="label">Expected</div>
          </div>
          <div class="summary-item">
            <div class="value">${job.summary.actualHours}h</div>
            <div class="label">Actual</div>
          </div>
          <div class="summary-item">
            <div class="value ${job.summary.variance > 0 ? 'variance-positive' : 'variance-negative'}">${job.summary.variance > 0 ? '+' : ''}${Math.round(job.summary.variance)}m</div>
            <div class="label">Variance</div>
          </div>
          <div class="summary-item">
            <div class="value">${job.summary.trackedSteps}/${job.summary.totalSteps}</div>
            <div class="label">Steps Tracked</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Operation</th>
              <th>Description</th>
              <th style="text-align:right">Expected</th>
              <th style="text-align:right">Actual</th>
              <th style="text-align:right">Tickets</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            ${job.routing.map(r => `
              <tr>
                <td><strong>${r.operationCode}</strong></td>
                <td>${r.description || '-'}</td>
                <td style="text-align:right">${r.estimatedMinutes}m</td>
                <td style="text-align:right">${r.actualMinutes}m</td>
                <td style="text-align:right">${r.ticketCount}</td>
                <td class="status-${r.status}">${r.status.replace('-', ' ')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="meta" style="margin-top: 20px;">
          Printed: ${new Date().toLocaleString()} | Employees: ${job.summary.totalEmployees} | Total Tickets: ${job.summary.totalTickets}
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getQualityColor = (score) => {
    if (score >= 95) return 'text-green-400';
    if (score >= 85) return 'text-yellow-400';
    if (score >= 70) return 'text-orange-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading data quality metrics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-700 rounded-lg p-4">
        <h3 className="text-red-400 font-medium">Error Loading Data Quality Metrics</h3>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchMetrics}
          className="mt-3 px-3 py-1 bg-red-700 hover:bg-red-600 rounded text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  const { summary } = metrics || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Data Quality Dashboard</h2>
          <p className="text-gray-500 text-sm">Post-launch validation and data quality monitoring</p>
        </div>
        <button
          onClick={fetchMetrics}
          className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Quality Score Banner */}
      {summary && (
        <div className="bg-slate-100 rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500 uppercase tracking-wide">Data Quality Score</div>
              <div className={`text-5xl font-bold ${getQualityColor(summary.qualityScore)}`}>
                {summary.qualityScore}%
              </div>
              <div className="text-sm text-gray-500 mt-1">Last 30 days</div>
            </div>
            <div className="grid grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-2xl font-semibold text-gray-900">{summary.completedTickets}</div>
                <div className="text-xs text-gray-500">Completed</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-blue-400">{summary.runningTickets}</div>
                <div className="text-xs text-gray-500">Running</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-yellow-400">{summary.pausedTickets}</div>
                <div className="text-xs text-gray-500">Paused</div>
              </div>
              <div>
                <div className="text-2xl font-semibold text-red-400">{summary.issueCount}</div>
                <div className="text-xs text-gray-500">Issues</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-4">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'orphan', label: `Orphan Tickets (${metrics?.orphanTickets?.length || 0})` },
            { id: 'zero', label: `Zero Duration (${metrics?.zeroDurationTickets?.length || 0})` },
            { id: 'excessive', label: `Excessive (${metrics?.excessiveDurationTickets?.length || 0})` },
            { id: 'incomplete', label: `Incomplete (${metrics?.incompleteDataTickets?.length || 0})` },
            { id: 'validation', label: 'Project Validation' },
            { id: 'routing', label: 'Job Routing' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-3 text-sm font-medium border-b-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-96">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Issue Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <IssueCard
                title="Orphan Tickets"
                count={summary?.orphanCount || 0}
                description="Running > 8 hours"
                color="orange"
                onClick={() => setActiveTab('orphan')}
              />
              <IssueCard
                title="Zero Duration"
                count={summary?.zeroDurationCount || 0}
                description="Completed with 0 time"
                color="red"
                onClick={() => setActiveTab('zero')}
              />
              <IssueCard
                title="Excessive Duration"
                count={summary?.excessiveDurationCount || 0}
                description="Completed > 8 hours"
                color="yellow"
                onClick={() => setActiveTab('excessive')}
              />
              <IssueCard
                title="Incomplete Data"
                count={summary?.incompleteDataCount || 0}
                description="Missing required fields"
                color="purple"
                onClick={() => setActiveTab('incomplete')}
              />
            </div>

            {/* Daily Trends */}
            <div className="bg-slate-100 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Daily Activity (Last 14 Days)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-gray-500 text-left">
                      <th className="py-2 px-3">Date</th>
                      <th className="py-2 px-3 text-right">Tickets</th>
                      <th className="py-2 px-3 text-right">Completed</th>
                      <th className="py-2 px-3 text-right">Active</th>
                      <th className="py-2 px-3 text-right">Employees</th>
                      <th className="py-2 px-3 text-right">Total Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics?.dailyTrends?.map((day, i) => (
                      <tr key={i} className="border-t border-gray-200 text-gray-700">
                        <td className="py-2 px-3">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                        <td className="py-2 px-3 text-right">{day.totalTickets}</td>
                        <td className="py-2 px-3 text-right text-green-400">{day.completedTickets}</td>
                        <td className="py-2 px-3 text-right text-blue-400">{day.activeTickets}</td>
                        <td className="py-2 px-3 text-right">{day.activeEmployees}</td>
                        <td className="py-2 px-3 text-right">{formatDuration(day.totalSeconds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'orphan' && (
          <TicketTable
            title="Orphan Tickets"
            description="Tickets running or paused for more than 8 hours - likely forgotten to complete"
            tickets={metrics?.orphanTickets || []}
            columns={['employeeId', 'jobNumber', 'operationCode', 'workCenter', 'quoteNumber', 'startTime', 'hoursRunning', 'state']}
            formatDuration={formatDuration}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'zero' && (
          <TicketTable
            title="Zero Duration Tickets"
            description="Completed tickets with zero duration - may indicate data entry errors"
            tickets={metrics?.zeroDurationTickets || []}
            columns={['employeeId', 'jobNumber', 'operationCode', 'workCenter', 'quoteNumber', 'startTime', 'endTime']}
            formatDuration={formatDuration}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'excessive' && (
          <TicketTable
            title="Excessive Duration Tickets"
            description="Completed tickets > 8 hours - may need review for accuracy"
            tickets={metrics?.excessiveDurationTickets || []}
            columns={['employeeId', 'jobNumber', 'operationCode', 'workCenter', 'quoteNumber', 'startTime', 'endTime', 'durationHours']}
            formatDuration={formatDuration}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'incomplete' && (
          <TicketTable
            title="Incomplete Data Tickets"
            description="Tickets missing required fields"
            tickets={metrics?.incompleteDataTickets || []}
            columns={['employeeId', 'jobNumber', 'operationCode', 'workCenter', 'quoteNumber', 'startTime', 'issueType']}
            formatDuration={formatDuration}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'validation' && (
          <div className="space-y-6">
            {/* Project Search */}
            <div className="bg-slate-100 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Project Validation</h3>
              <p className="text-sm text-gray-500 mb-4">
                Enter a quote number to see all logged time and compare against expected operations.
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={quoteNumber}
                  onChange={(e) => setQuoteNumber(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && fetchProjectValidation()}
                  placeholder="Enter quote number (e.g., Q12345)"
                  className="flex-1 bg-white border border-gray-300 rounded px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500"
                />
                <button
                  onClick={fetchProjectValidation}
                  disabled={validationLoading || !quoteNumber.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed rounded text-gray-900 font-medium"
                >
                  {validationLoading ? 'Loading...' : 'Validate'}
                </button>
              </div>
            </div>

            {validationError && (
              <div className="bg-red-100 border border-red-700 rounded-lg p-4">
                <p className="text-red-600">{validationError}</p>
              </div>
            )}

            {projectValidation && (
              <ProjectValidationResults
                data={projectValidation}
                formatDuration={formatDuration}
                formatDate={formatDate}
              />
            )}
          </div>
        )}

        {activeTab === 'routing' && (
          <div className="space-y-6">
            {/* Quote Search */}
            <div className="bg-slate-100 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Job Routing Comparison</h3>
              <p className="text-sm text-gray-500 mb-4">
                Compare expected routing from JobBoss against actual time tickets. Click a job to see detailed routing.
              </p>
              {/* Fuzzy Search with Dropdown */}
              <div className="relative">
                {selectedQuote ? (
                  /* Selected Quote Display */
                  <div className="flex items-center gap-3 bg-white border border-gray-300 rounded px-3 py-2">
                    <div className="flex-1">
                      <span className="text-gray-900 font-medium">{selectedQuote.quoteNumber}</span>
                      {selectedQuote.user_Text4 && (
                        <span className="text-gray-500 ml-2">- {selectedQuote.user_Text4}</span>
                      )}
                      {selectedQuote.customerName && (
                        <span className="text-gray-500 text-sm ml-2">({selectedQuote.customerName})</span>
                      )}
                    </div>
                    <button
                      onClick={clearQuoteSelection}
                      className="text-gray-500 hover:text-gray-900 px-2"
                      title="Clear selection"
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  /* Search Input with Dropdown */
                  <>
                    <input
                      ref={quoteSearchRef}
                      type="text"
                      value={quoteNumber}
                      onChange={(e) => {
                        setQuoteNumber(e.target.value);
                        setShowQuoteDropdown(true);
                      }}
                      onFocus={() => setShowQuoteDropdown(true)}
                      placeholder={quotesLoading ? 'Loading quotes...' : 'Search by quote number, project name, or customer...'}
                      className="w-full bg-white border border-gray-300 rounded px-3 py-2 text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />

                    {/* Dropdown Results */}
                    {showQuoteDropdown && !quotesLoading && (
                      <div
                        ref={quoteDropdownRef}
                        className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto bg-slate-100 border border-gray-300 rounded-lg shadow-lg"
                      >
                        {filteredQuotes.length === 0 ? (
                          <div className="px-4 py-3 text-gray-500 text-sm">
                            {quoteNumber.trim() ? 'No quotes found' : 'Type to search quotes...'}
                          </div>
                        ) : (
                          filteredQuotes.map((quote) => (
                            <button
                              key={quote.quoteNumber}
                              onClick={() => handleQuoteSelect(quote)}
                              className="w-full text-left px-4 py-2 hover:bg-slate-100 border-b border-gray-200 last:border-b-0 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-gray-900 font-medium">{quote.quoteNumber}</span>
                                {quote.customerName && (
                                  <span className="text-gray-500 text-xs">{quote.customerName}</span>
                                )}
                              </div>
                              {quote.user_Text4 && (
                                <div className="text-gray-500 text-sm truncate">{quote.user_Text4}</div>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {routingError && (
              <div className="bg-red-100 border border-red-700 rounded-lg p-4">
                <p className="text-red-600">{routingError}</p>
              </div>
            )}

            {routingComparison && (
              <JobRoutingComparison
                data={routingComparison}
                selectedJob={selectedJob}
                setSelectedJob={setSelectedJob}
                onPrintJob={handlePrintJob}
                formatDate={formatDate}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Issue Card Component
function IssueCard({ title, count, description, color, onClick }) {
  const colorClasses = {
    red: 'border-red-700 bg-red-900/30',
    orange: 'border-orange-700 bg-orange-900/30',
    yellow: 'border-yellow-700 bg-yellow-900/30',
    purple: 'border-purple-700 bg-purple-900/30',
  };

  const textClasses = {
    red: 'text-red-400',
    orange: 'text-orange-400',
    yellow: 'text-yellow-400',
    purple: 'text-purple-600',
  };

  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-lg border ${colorClasses[color]} hover:opacity-80 transition-opacity text-left`}
    >
      <div className={`text-3xl font-bold ${textClasses[color]}`}>{count}</div>
      <div className="text-gray-900 font-medium mt-1">{title}</div>
      <div className="text-gray-500 text-xs mt-1">{description}</div>
    </button>
  );
}

// Ticket Table Component
function TicketTable({ title, description, tickets, columns, formatDuration, formatDate }) {
  const columnLabels = {
    employeeId: 'Employee',
    jobNumber: 'Job #',
    operationCode: 'Operation',
    workCenter: 'Work Center',
    quoteNumber: 'Quote',
    startTime: 'Started',
    endTime: 'Ended',
    hoursRunning: 'Hours Running',
    durationHours: 'Duration (hrs)',
    state: 'State',
    issueType: 'Issue',
  };

  if (tickets.length === 0) {
    return (
      <div className="bg-slate-100 rounded-lg p-6 border border-gray-200 text-center">
        <div className="text-green-400 text-lg font-medium">No Issues Found</div>
        <div className="text-gray-500 text-sm mt-1">All tickets in this category look good!</div>
      </div>
    );
  }

  return (
    <div className="bg-slate-100 rounded-lg border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-gray-500 text-left">
              {columns.map(col => (
                <th key={col} className="py-3 px-4">{columnLabels[col] || col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tickets.map((ticket, i) => (
              <tr key={ticket.id || i} className="border-t border-gray-200 text-gray-700 hover:bg-slate-100/50">
                {columns.map(col => (
                  <td key={col} className="py-3 px-4">
                    {col === 'startTime' || col === 'endTime'
                      ? formatDate(ticket[col])
                      : col === 'hoursRunning' || col === 'durationHours'
                      ? `${ticket[col]}h`
                      : col === 'state'
                      ? <span className={ticket[col] === 'running' ? 'text-blue-400' : 'text-yellow-400'}>{ticket[col]}</span>
                      : ticket[col] || '-'
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-3 border-t border-gray-200 text-gray-500 text-sm">
        Showing {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

// Project Validation Results Component
function ProjectValidationResults({ data, formatDuration, formatDate }) {
  const { summary, byJob, byWorkCenter, byOperation } = data;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-slate-100 rounded-lg p-4 border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {summary.quoteNumber} - {summary.projectName || 'Unknown Project'}
        </h3>
        <div className="grid grid-cols-6 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.totalHours}h</div>
            <div className="text-xs text-gray-500">Total Hours</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.totalJobs}</div>
            <div className="text-xs text-gray-500">Jobs</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.totalOperations}</div>
            <div className="text-xs text-gray-500">Operations</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.totalTickets}</div>
            <div className="text-xs text-gray-500">Tickets</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.totalEmployees}</div>
            <div className="text-xs text-gray-500">Employees</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-gray-700">{formatDate(summary.firstActivity)}</div>
            <div className="text-xs text-gray-500">First Activity</div>
          </div>
        </div>
      </div>

      {/* By Work Center */}
      <div className="bg-slate-100 rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-900">Time by Work Center</h4>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-gray-500 text-left">
              <th className="py-2 px-4">Work Center</th>
              <th className="py-2 px-4 text-right">Hours</th>
              <th className="py-2 px-4 text-right">Jobs</th>
              <th className="py-2 px-4 text-right">Operations</th>
              <th className="py-2 px-4 text-right">Tickets</th>
            </tr>
          </thead>
          <tbody>
            {byWorkCenter.map((wc, i) => (
              <tr key={i} className="border-t border-gray-200 text-gray-700">
                <td className="py-2 px-4 font-medium">{wc.workCenter}</td>
                <td className="py-2 px-4 text-right">{wc.totalHours}h</td>
                <td className="py-2 px-4 text-right">{wc.jobCount}</td>
                <td className="py-2 px-4 text-right">{wc.operationCount}</td>
                <td className="py-2 px-4 text-right">{wc.ticketCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* By Operation */}
      <div className="bg-slate-100 rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-900">Time by Operation</h4>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-gray-500 text-left">
              <th className="py-2 px-4">Operation</th>
              <th className="py-2 px-4">Work Center</th>
              <th className="py-2 px-4 text-right">Hours</th>
              <th className="py-2 px-4 text-right">Jobs</th>
              <th className="py-2 px-4 text-right">Tickets</th>
            </tr>
          </thead>
          <tbody>
            {byOperation.map((op, i) => (
              <tr key={i} className="border-t border-gray-200 text-gray-700">
                <td className="py-2 px-4 font-mono">{op.operationCode}</td>
                <td className="py-2 px-4">{op.workCenter}</td>
                <td className="py-2 px-4 text-right">{op.totalHours}h</td>
                <td className="py-2 px-4 text-right">{op.jobCount}</td>
                <td className="py-2 px-4 text-right">{op.ticketCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* By Job */}
      <div className="bg-slate-100 rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-900">Time by Job (SKU)</h4>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white text-gray-500 text-left">
              <th className="py-2 px-4">Job #</th>
              <th className="py-2 px-4">Name</th>
              <th className="py-2 px-4">Order</th>
              <th className="py-2 px-4 text-right">Hours</th>
              <th className="py-2 px-4 text-right">Operations</th>
              <th className="py-2 px-4 text-right">Tickets</th>
            </tr>
          </thead>
          <tbody>
            {byJob.map((job, i) => (
              <tr key={i} className="border-t border-gray-200 text-gray-700">
                <td className="py-2 px-4 font-mono">{job.jobNumber}</td>
                <td className="py-2 px-4">{job.jobName || '-'}</td>
                <td className="py-2 px-4">{job.orderNumber}</td>
                <td className="py-2 px-4 text-right">{job.totalHours}h</td>
                <td className="py-2 px-4 text-right">{job.operationCount}</td>
                <td className="py-2 px-4 text-right">{job.ticketCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Job Routing Comparison Component
function JobRoutingComparison({ data, selectedJob, setSelectedJob, onPrintJob, formatDate }) {
  const { summary, jobs } = data;

  // Simplified status: just logged or not logged
  const getLoggedStatus = (hasTimeLogged) => {
    return hasTimeLogged ? { text: 'Logged', color: 'text-green-400', icon: '\u2713' }
                         : { text: 'Not Logged', color: 'text-red-400', icon: '\u2717' };
  };

  // Format employee list (show first name only, comma separated)
  const formatEmployees = (employees) => {
    if (!employees || employees.length === 0) return '-';
    // Extract first names from employee IDs (assumes format like "john.smith" or just "john")
    const names = employees.map(emp => {
      const parts = emp.split('.');
      return parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    });
    return names.join(', ');
  };

  // Print all jobs for the entire quote
  const handlePrintAll = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Job Routing Report - Quote ${summary.quoteNumber}</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 12px; padding: 20px; }
          h1 { font-size: 18px; margin-bottom: 5px; }
          h2 { font-size: 14px; margin: 20px 0 10px 0; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
          .summary { margin-bottom: 20px; background: #f5f5f5; padding: 10px; border-radius: 4px; }
          .summary-grid { display: flex; gap: 30px; }
          .summary-item { text-align: center; }
          .summary-value { font-size: 16px; font-weight: bold; }
          .summary-label { font-size: 10px; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 15px; }
          th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
          th { background: #f0f0f0; font-weight: bold; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .logged { color: green; }
          .not-logged { color: red; }
          .subtotal { background: #f5f5f5; font-weight: bold; }
          .job-header { background: #e8e8e8; }
          .page-break { page-break-after: always; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <h1>Job Routing Report</h1>
        <p>Quote: ${summary.quoteNumber} | Project: ${summary.projectName || 'N/A'} | Customer: ${summary.customerCode || 'N/A'}</p>

        <div class="summary">
          <div class="summary-grid">
            <div class="summary-item">
              <div class="summary-value">${jobs.length}</div>
              <div class="summary-label">Jobs</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${summary.totalSteps}</div>
              <div class="summary-label">Routing Steps</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${formatMinutesAsTime(summary.totalExpectedMinutes || 0)}</div>
              <div class="summary-label">Total Expected</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${formatMinutesAsTime(summary.totalActualMinutes || 0)}</div>
              <div class="summary-label">Total Actual</div>
            </div>
            <div class="summary-item">
              <div class="summary-value">${summary.totalVariance > 0 ? '+' : ''}${formatMinutesAsTime(summary.totalVariance || 0)}</div>
              <div class="summary-label">Total Variance</div>
            </div>
          </div>
        </div>

        ${jobs.map(job => `
          <h2>Job ${job.jobNumber} - ${job.partNumber} (Qty: ${job.qty})</h2>
          <p style="color: #666; margin: 0 0 10px 0;">${job.description || 'No description'}</p>
          <table>
            <thead>
              <tr>
                <th>Operation</th>
                <th>Description</th>
                <th class="text-right">Expected</th>
                <th class="text-right">Actual</th>
                <th class="text-right">Variance</th>
                <th>Employee</th>
                <th class="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              ${job.routing.map(r => `
                <tr>
                  <td>${r.operationCode}</td>
                  <td>${r.description || '-'}</td>
                  <td class="text-right">${formatMinutesAsTime(r.estimatedMinutes)}</td>
                  <td class="text-right">${formatMinutesAsTime(r.actualMinutes)}</td>
                  <td class="text-right">${r.variance > 0 ? '+' : ''}${formatMinutesAsTime(r.variance)}</td>
                  <td>${formatEmployees(r.employees)}</td>
                  <td class="text-center ${r.hasTimeLogged ? 'logged' : 'not-logged'}">${r.hasTimeLogged ? '\u2713 Logged' : '\u2717 Not Logged'}</td>
                </tr>
              `).join('')}
              <tr class="subtotal">
                <td colspan="2">Job Subtotal</td>
                <td class="text-right">${formatMinutesAsTime(job.summary.expectedMinutes)}</td>
                <td class="text-right">${formatMinutesAsTime(job.summary.actualMinutes)}</td>
                <td class="text-right">${job.summary.variance > 0 ? '+' : ''}${formatMinutesAsTime(job.summary.variance)}</td>
                <td colspan="2"></td>
              </tr>
            </tbody>
          </table>
        `).join('')}

        <p style="margin-top: 30px; font-size: 10px; color: #999;">
          Generated: ${new Date().toLocaleString()} | 3D Color Time Tracker
        </p>
      </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  const getVarianceClass = (variance) => {
    if (variance > 0) return 'text-red-400';
    if (variance < 0) return 'text-green-400';
    return 'text-gray-500';
  };

  if (!jobs || jobs.length === 0) {
    return (
      <div className="bg-slate-100 rounded-lg p-6 border border-gray-200 text-center">
        <div className="text-yellow-400 text-lg font-medium">No Jobs Found</div>
        <div className="text-gray-500 text-sm mt-1">No jobs found for this quote number.</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Project Summary */}
      <div className="bg-slate-100 rounded-lg p-4 border border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Quote {summary.quoteNumber} - {summary.projectName || 'Unknown Project'}
          </h3>
          <button
            onClick={handlePrintAll}
            className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded text-sm font-medium"
          >
            Print All Jobs
          </button>
        </div>
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.customerCode || '-'}</div>
            <div className="text-xs text-gray-500">Customer</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.totalJobs}</div>
            <div className="text-xs text-gray-500">Jobs</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.totalSteps}</div>
            <div className="text-xs text-gray-500">Routing Steps</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.totalTickets}</div>
            <div className="text-xs text-gray-500">Time Tickets</div>
          </div>
        </div>
        {/* Quote Time Subtotals */}
        <div className="border-t border-gray-300 pt-4">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center bg-gray-700/50 rounded-lg p-3">
              <div className="text-2xl font-bold font-mono text-gray-900">
                {formatMinutesAsTime(summary.totalExpectedMinutes || 0)}
              </div>
              <div className="text-xs text-gray-500">Total Expected</div>
            </div>
            <div className="text-center bg-gray-700/50 rounded-lg p-3">
              <div className="text-2xl font-bold font-mono text-gray-900">
                {formatMinutesAsTime(summary.totalActualMinutes || 0)}
              </div>
              <div className="text-xs text-gray-500">Total Actual</div>
            </div>
            <div className="text-center bg-gray-700/50 rounded-lg p-3">
              <div className={`text-2xl font-bold font-mono ${getVarianceClass(summary.totalVariance)}`}>
                {summary.totalVariance > 0 ? '+' : ''}{formatMinutesAsTime(summary.totalVariance)}
              </div>
              <div className="text-xs text-gray-500">Total Variance</div>
            </div>
          </div>
          {/* Attended vs Unattended Time Breakdown */}
          {(summary.totalAttendedMinutes > 0 || summary.totalUnattendedMinutes > 0) && (
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="text-xl font-bold font-mono text-blue-600">
                  {formatMinutesAsTime(summary.totalAttendedMinutes || 0)}
                </div>
                <div className="text-xs text-blue-400">Attended Time</div>
              </div>
              <div className="text-center bg-orange-900/30 border border-orange-700/50 rounded-lg p-3">
                <div className="text-xl font-bold font-mono text-orange-600">
                  {formatMinutesAsTime(summary.totalUnattendedMinutes || 0)}
                </div>
                <div className="text-xs text-orange-400">Machine Time (Unattended)</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Jobs List */}
      <div className="bg-slate-100 rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h4 className="font-medium text-gray-900">Jobs ({jobs.length})</h4>
          <p className="text-xs text-gray-500 mt-1">Click a job to see routing comparison</p>
        </div>

        <div className="divide-y divide-gray-200">
          {jobs.map((job) => (
            <div key={job.jobNumber}>
              {/* Job Header Row */}
              <button
                onClick={() => setSelectedJob(selectedJob === job.jobNumber ? null : job.jobNumber)}
                className={`w-full p-4 text-left hover:bg-slate-100/50 transition-colors ${
                  selectedJob === job.jobNumber ? 'bg-gray-700/50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-medium text-gray-900">{job.jobNumber}</span>
                      <span className="text-gray-500">{job.partNumber}</span>
                      <span className="text-xs text-gray-500">Qty: {job.qty}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Order: {job.orderNumber} | {job.description || 'No description'}
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <div className="text-gray-900 font-mono">{formatMinutesAsTime(job.summary.expectedMinutes)}</div>
                      <div className="text-xs text-gray-500">Expected</div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-900 font-mono">{formatMinutesAsTime(job.summary.actualMinutes)}</div>
                      <div className="text-xs text-gray-500">Actual</div>
                    </div>
                    <div className="text-center">
                      <div className={`font-mono ${getVarianceClass(job.summary.variance)}`}>
                        {job.summary.variance > 0 ? '+' : ''}{formatMinutesAsTime(job.summary.variance)}
                      </div>
                      <div className="text-xs text-gray-500">Variance</div>
                    </div>
                    <div className="text-center">
                      <div className="text-gray-900">{job.summary.trackedSteps}/{job.summary.totalSteps}</div>
                      <div className="text-xs text-gray-500">Tracked</div>
                    </div>
                    <div className="w-5 text-gray-500">
                      {selectedJob === job.jobNumber ? '\u25BC' : '\u25B6'}
                    </div>
                  </div>
                </div>
              </button>

              {/* Expanded Job Detail */}
              {selectedJob === job.jobNumber && (
                <div className="p-4 bg-white border-t border-gray-200">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="text-gray-900 font-medium">Routing Comparison</h5>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onPrintJob(job);
                      }}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 rounded text-sm font-medium"
                    >
                      Print Report
                    </button>
                  </div>

                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 text-left">
                        <th className="py-2 px-3">Operation</th>
                        <th className="py-2 px-3">Description</th>
                        <th className="py-2 px-3 text-right">Expected</th>
                        <th className="py-2 px-3 text-right">Actual</th>
                        <th className="py-2 px-3 text-right">Variance</th>
                        <th className="py-2 px-3 text-right">Tickets</th>
                        <th className="py-2 px-3">Employee</th>
                        <th className="py-2 px-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {job.routing.map((r, i) => {
                        const status = getLoggedStatus(r.hasTimeLogged);
                        return (
                          <tr key={i} className={`border-t border-gray-200 text-gray-700 ${r.isUnattended ? 'bg-orange-900/10' : ''}`}>
                            <td className="py-2 px-3">
                              <span className="font-mono font-medium">{r.operationCode}</span>
                              {r.isUnattended && (
                                <span className="ml-2 text-xs text-orange-400" title="Machine Time (Unattended)">M</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-gray-500">{r.description || '-'}</td>
                            <td className="py-2 px-3 text-right font-mono">{formatMinutesAsTime(r.estimatedMinutes)}</td>
                            <td className="py-2 px-3 text-right font-mono">{formatMinutesAsTime(r.actualMinutes)}</td>
                            <td className={`py-2 px-3 text-right font-mono ${getVarianceClass(r.variance)}`}>
                              {r.variance > 0 ? '+' : ''}{formatMinutesAsTime(r.variance)}
                            </td>
                            <td className="py-2 px-3 text-right">{r.ticketCount}</td>
                            <td className="py-2 px-3 text-gray-500">{formatEmployees(r.employees)}</td>
                            <td className={`py-2 px-3 text-center ${status.color}`}>
                              <span>{status.icon} {status.text}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {/* Job Subtotal Row */}
                    <tfoot>
                      <tr className="border-t-2 border-gray-500 bg-gray-700/50 text-gray-900 font-semibold">
                        <td className="py-2 px-3" colSpan="2">
                          Job Subtotal
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {formatMinutesAsTime(job.summary.expectedMinutes)}
                        </td>
                        <td className="py-2 px-3 text-right font-mono">
                          {formatMinutesAsTime(job.summary.actualMinutes)}
                        </td>
                        <td className={`py-2 px-3 text-right font-mono ${getVarianceClass(job.summary.variance)}`}>
                          {job.summary.variance > 0 ? '+' : ''}{formatMinutesAsTime(job.summary.variance)}
                        </td>
                        <td className="py-2 px-3 text-right">
                          {job.summary.totalTickets}
                        </td>
                        <td className="py-2 px-3"></td>
                        <td className="py-2 px-3 text-center text-gray-500">
                          —
                        </td>
                      </tr>
                    </tfoot>
                  </table>

                  {/* Status Legend */}
                  <div className="flex gap-4 mt-4 text-xs flex-wrap">
                    <span className="text-green-400">✓ Logged: Time tickets recorded</span>
                    <span className="text-red-400">✗ Not Logged: No time tracked yet</span>
                    <span className="text-orange-400">M = Machine Time (Unattended)</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
